import { Alert, ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import { PeoplePicker, PersonType, UserType } from '@microsoft/mgt-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appState } from '../../../AppState';
import { getCachedFeature } from '../../../shared/cache/FeatureCache';
import { getCachedUsers } from '../../../shared/cache/PlatformUserCache';
import { FilterAccordionValues, FilterPropsItems, getSearchedValues } from '../../../shared/components/Filter';
import { LabelElement } from '../../../shared/components/TicketForm';
import { FILTERS, FilterDataType, LICENSE_FEATURE, ScopeTokens } from '../../../shared/utils/constants';
import { CustomViewContext } from '../CustomViewContextProvider';
import ToolbarCSS from '../Toolbar.module.css';
import { CustomViewFilter } from './CustomViewFilter/CustomViewFilter';
import { getChildFiltersCountFromStorage, inValidAffectedUsers, inValidRequesters, isChildFilterSelectedExceptQueryValue } from './heplers/customPopupHelper';
import { ThemeColorScheme } from '../../../shared/common/TeamsTheme';
import { CustomPeoplePicker } from '../../../shared/components/CustomPeoplePicker/CustomPeoplePicker';

interface Props {
  defaultFilters: FilterPropsItems[];
  selectedRequesters: PeoplePickerUserType[];
  setSelectedRequesters: React.Dispatch<React.SetStateAction<PeoplePickerUserType[]>>;
  invalidRequesterMsg: string;
  setInvalidRequesterMsg: React.Dispatch<React.SetStateAction<string>>;
  selectedAffectedUsers: PeoplePickerUserType[];
  setSelectedAffectedUsers: React.Dispatch<React.SetStateAction<PeoplePickerUserType[]>>;
  invalidAffectedUserMsg: string;
  setInvalidAffectedUserMsg: React.Dispatch<React.SetStateAction<string>>;
  filtersByFeature: (item: any, field: string) => void;
}

const Filters = ({
  defaultFilters,
  selectedRequesters,
  setSelectedRequesters,
  invalidRequesterMsg,
  setInvalidRequesterMsg,
  selectedAffectedUsers,
  setSelectedAffectedUsers,
  invalidAffectedUserMsg,
  setInvalidAffectedUserMsg,
  filtersByFeature
}: Props) => {
  const state = appState();

  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);
  const { filters } = customViewStates;
  const [localFilters, setLocalFilters] = useState([] as FilterPropsItems[])
  const [searchValues, setSearchValues] = useState<Record<number, string>>({});
  const featureRestrictFilters = filters.filter(item => filtersByFeature(item, "title"));
  const { t } = useTranslation();

  const onClickSelectedValues = (filter: FilterPropsItems, clickedValue: FilterAccordionValues, date?: any) => {
    const isSearchDropdown = filter.id === FILTERS.ASSIGNEE.id;
    const selectedFilterIdx = filters.findIndex((f) => f.id === filter.id);
    const selectedFilter = filters[selectedFilterIdx];
    const selectedValue = selectedFilter.values.find((value) => value.key === clickedValue.key);

    if (filter.FilterDataType == FilterDataType.Boolean) selectedFilter.values.forEach(i => {
      if (i.key != clickedValue.key) i.selected = false;
    });

    if (filter.id == FILTERS.CREATED_DATE.id || filter.id == FILTERS.MODIFIED_DATE.id || filter.FilterDataType == FilterDataType.Date || filter.FilterDataType == FilterDataType.Time || filter.FilterDataType == FilterDataType.Number) {
      //checking if there is a field selected then limiting selection on field dropdown
      const customValueOperatorIsAlreadySelected = filter?.values?.find(x => x.selected);
      const propKey: string = filter.FilterDataType == FilterDataType.Number ? "number" : "date";
      let temp = selectedFilter.values
      .map((value) => {
        if (value.key === clickedValue.key) {
          value.selected = !value.selected;
          value[propKey] = customValueOperatorIsAlreadySelected ? customValueOperatorIsAlreadySelected[propKey] : null;
        }
        else {
          value.selected = false;
          delete value[propKey];
        }
        return value;
      });
      selectedFilter.values = temp;
    }
    else {
      selectedValue.selected = !selectedValue.selected;
    }
    setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, filters: JSON.parse(JSON.stringify(filters)) }));

    let dropdownValues = [];
    if (isSearchDropdown) {
      dropdownValues = getSearchedValues([...selectedFilter.values], searchValues[filter.id] ?? '');
    } else {
      dropdownValues = selectedFilter.values;
    }

    const localSelectedFilter = localFilters[selectedFilterIdx];
    localSelectedFilter.values = dropdownValues;

    setLocalFilters([...localFilters]);
  };

  const onChangeSelectedValue = (clickedParentId: number, clickedValue: string, propertyKey: string) => {
    const selectedFilterIdx = filters.findIndex((filter) => filter.id === clickedParentId);
    const selectedFilter = filters[selectedFilterIdx];
    const selectedValue = selectedFilter.values.find((value) => value.selected);
    selectedValue[propertyKey] = clickedValue;
    setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, filters: JSON.parse(JSON.stringify(filters)) }));
    setLocalFilters(filters);
  }
  interface IGetFilterCountReturn {
    [key: string]: number
  }
  const getFilterCount = useCallback((filtersArr: FilterPropsItems[] = JSON.parse(JSON.stringify(filters))): IGetFilterCountReturn => {
    let totalSelectedCount = 0;

    const deleteFilterIfExists = (id: number) => {
      // find index of filter from filters array
      const userFilterIdx = filtersArr.findIndex((filter: any) => filter.id === id);
      // delete filter from filters array, if exists
      if (userFilterIdx !== -1) {
        filtersArr.splice(userFilterIdx, 1);
      }
    }

    deleteFilterIfExists(FILTERS.REQUESTERS.id);
    deleteFilterIfExists(FILTERS.AffectedUsers.id);

    const selectedCount = filtersArr?.reduce((acc, filter) => {
      const selectedValueCount = filter?.values?.reduce((acc: any, value: any) => {
        acc = value.selected ? acc + 1 : acc;
        return acc;
      }, 0);

      totalSelectedCount += selectedValueCount;

      return { ...acc, [filter.id]: selectedValueCount };
    }, {});

    selectedCount['total'] = totalSelectedCount + selectedRequesters.length + selectedAffectedUsers.length;

    return selectedCount;
  }, [filters, selectedRequesters, selectedAffectedUsers]);

  useEffect(() => {
    (async () => {
      const enabledTicketTags = await getCachedFeature(LICENSE_FEATURE.TicketTags);
      if (!enabledTicketTags) defaultFilters = defaultFilters.filter(x => x.key !== 'Tags');
      if (defaultFilters && customViewStates.filters?.length === 0) {
        setCustomViewStates((prev): CustomViewStatesType => ({
          ...prev, filters: [...JSON.parse(JSON.stringify(defaultFilters))],
        }));
      }
      if (customViewStates.view !== 'edit' && defaultFilters && localFilters.length === 0) {
        setLocalFilters([...JSON.parse(JSON.stringify(defaultFilters))])
      }

      if (customViewStates.view === 'edit') {
        const tempLocalFilters = [...JSON.parse(JSON.stringify(featureRestrictFilters))]
        for (const indx of [FILTERS.ASSIGNEE.id, FILTERS.TicketCollaborators.id]) {
          const selectedFilterIdx = tempLocalFilters.findIndex((filter) =>  filter.id === indx);
          const localSelectedFilter = tempLocalFilters[selectedFilterIdx]
          const dropdownValues = getSearchedValues([...localSelectedFilter.values], '')
          localSelectedFilter.values = dropdownValues
        }

        setSearchValues({})
        setLocalFilters([...tempLocalFilters])
      }

      if (
        isChildFilterSelectedExceptQueryValue(
          await getChildFiltersCountFromStorage(),
          state.filterDropdown?.queryValueApplied
        ) && customViewStates.filters?.length > 0
      ) {
        setLocalFilters(JSON.parse(JSON.stringify(customViewStates.filters)))
      }
    })()

  }, [defaultFilters]);

  const defaultSelectedUsersFromFilterValues = async (filteredValues: null | FilterAccordionValues[]) => {
    if (!filteredValues) return [];
    const users = await getCachedUsers(filteredValues.map(x => x.key));
    let tempList = users.map(u => ({
      displayName: u.FullName,
      userPrincipalName: u.Email,
      givenName: u.FullName,
      surname: u.FamilyName,
      mail: u.Email,
      id: u.AadObjectId
    }));

    if (filteredValues.some(x=>x.key==-1))
      tempList.unshift({ ...ScopeTokens.MeTokenData, displayName: t('common.me') });

    return tempList;
  }

  // set initial selectedRequesters in case of custom_view editing or saving(when customViewStates.view is equal to 'edit' or 'save')
  useEffect(() => {
    if (customViewStates.view !== 'edit' && customViewStates.view !== 'save') {
      setSelectedRequesters([]);
      setSelectedAffectedUsers([])
      return;
    }

    if (customViewStates.view === 'edit' || customViewStates.view === 'save') {
      const setPickers = async () => {
        let requesterValues = customViewStates.filters.find((filteredItem) => filteredItem.id === FILTERS.REQUESTERS.id);
        if (requesterValues?.values && requesterValues?.values?.length !== 0) {
          let filteredRequesterValues = requesterValues.values.filter((item) => item.selected);
          const requesters = await defaultSelectedUsersFromFilterValues(filteredRequesterValues);
          setSelectedRequesters(requesters);
        }

        let affectedUserValues = customViewStates.filters.find((filteredItem) => filteredItem.id === FILTERS.AffectedUsers.id);
        if (affectedUserValues?.values && affectedUserValues?.values?.length !== 0) {
          let filteredaffectedUserValues = affectedUserValues.values.filter((item) => item.selected);
          const selectedAffectedUser = await defaultSelectedUsersFromFilterValues(filteredaffectedUserValues);
          setSelectedAffectedUsers(selectedAffectedUser);
        }
      };
      
      setPickers();
    };
  }, []);

  const selectRequestersFunc = () => {
    return selectedRequesters;
  };

  const selectAffectedUsersFunc = () => {
    return selectedAffectedUsers;
  };

  const onChangeSearchDropdown = (e: any) => {
    const value: string = e.target.value;
    setSearchValues(prev => ({ ...prev, [FILTERS.ASSIGNEE.id]: value }))

    const filterIdx = filters.findIndex(filter => filter.title === FILTERS.ASSIGNEE.title)
    const values = filters[filterIdx].values
    const dropdownValues = getSearchedValues([...values], value)

    localFilters[filterIdx].values = dropdownValues

    setLocalFilters((_prev) => ([...localFilters]))
  }

  const onChangeCollaboratorSearchDropdown = (e: any) => {
    const value: string = e.target.value;
    setSearchValues(prev => ({ ...prev, [FILTERS.TicketCollaborators.id]: value }))

    const filterIdx = filters.findIndex(filter => filter.title === FILTERS.TicketCollaborators.title)
    const values = filters[filterIdx].values
    const dropdownValues = getSearchedValues([...values], value)

    localFilters[filterIdx].values = dropdownValues

    setLocalFilters((_prev) => ([...localFilters]))
  }

  const onTextChange = (e: any, filterId: number) => {
    const value: string = e.value;
    const filterIdx = filters.findIndex(filter => filter.id === filterId)
    localFilters[filterIdx].stringValue = value;
    setLocalFilters((_prev) => ([...localFilters]))
    setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, filters: JSON.parse(JSON.stringify(localFilters)) }));
  }

  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => {
        return (
          <div style={{ height: '100%' }} >
            <div className='font-semibold mt-3'>
              Filters{' '}{getFilterCount()['total'] > 0 ? `(${getFilterCount()['total']})` : ``}
            </div>
            {invalidRequesterMsg && (
              <Alert
                fitted
                danger={true}
                content={invalidRequesterMsg}
                style={{
                  fontSize: 12,
                  width: '100%',
                  marginBottom: '.5rem',
                  marginTop: '1rem',
                }}
              />
            )}
            <div className='flex flex-col mt-2'>
              <div className='flex flex-wrap gap-2 mt-2'>
                <div className={`${ToolbarCSS.filterItem} relative`}>
                  <div>
                    <LabelElement label={`${t('ticket.ticket-list.configure-column.requesters')} ${selectedRequesters.length > 0 ? '(' + selectedRequesters.length + ')' : ''}`}>
                        <div style={{display: 'block',...{ '--input-background-color': 'var(--mgt-theme-background)' }, ...ThemeColorScheme(globalTheme.siteVariables)}}>
                          <CustomPeoplePicker
                            enableMeToken={true}
                            userType={UserType.user}
                            type={PersonType.person}
                            isEnabled={true}
                            selectedPeople={selectRequestersFunc()}
                            selectionChanged={(e)=>{
                              const detail: PeoplePickerUserType[] = e.detail.map(u => ({ displayName: u.displayName, mail: u.mail, givenName: u.givenName, surname: u.surname, userPrincipalName: u.userPrincipalName, id: u.id }));
                              inValidRequesters(detail, setInvalidRequesterMsg) // check
                              setSelectedRequesters([...detail]);
                            }}
                          />
                        </div>
                    </LabelElement>
                  </div>
                </div>
                <div className={`${ToolbarCSS.filterItem} relative`}>
                  <div>
                    <LabelElement label={`${t('ticket.ticket-list.configure-column.affected-users')} ${selectedAffectedUsers.length > 0 ? '(' + selectedAffectedUsers.length + ')' : ''}`}></LabelElement>
                    <div style={{display: 'block',...{ '--input-background-color': 'var(--mgt-theme-background)' }, ...ThemeColorScheme(globalTheme.siteVariables)}}>
                      <CustomPeoplePicker
                        enableMeToken={true}
                        userType={UserType.user}
                        type={PersonType.person}
                        isEnabled={true}
                        selectedPeople={selectAffectedUsersFunc()}
                        selectionChanged={(e)=>{
                           const detail: PeoplePickerUserType[] = e.detail;
                          inValidAffectedUsers(detail, setInvalidAffectedUserMsg) // check
                          setSelectedAffectedUsers([...detail]);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className='grid grid-cols-3 gap-2 mt-2'>
                  {localFilters
                    .filter((filter) => ![FILTERS.REQUESTERS.id, FILTERS.AffectedUsers.id].includes(filter.id)).sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((filter: FilterPropsItems) => {
                      return <CustomViewFilter
                        key={filter.id}
                        filter={filter}
                        searchValue={searchValues[filter.id] ?? ''}
                        globalTheme={globalTheme}
                        getFilterCount={getFilterCount}
                        onChangeSearchDropdown={onChangeSearchDropdown}
                        onClick={(value, date) => onClickSelectedValues(filter, value, date)}
                        onChangeCollaboratorSearchDropdown={onChangeCollaboratorSearchDropdown}
                        onChangeSelectedValue={(value, propKey) => onChangeSelectedValue(filter.id, value, propKey)}
                        onTextChange={onTextChange} />
                    })}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

export default Filters;
